<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import BButton from "@/components/common/bButton.vue";
import CandidateDocumentButton from "@/components/recruitment/interviews/CandidateDocumentButton.vue";
import CandidateDocumentContainer from "@/components/recruitment/interviews/CandidateDocumentContainer.vue";
import SolarCloudDownloadLineDuotone from "~icons/solar/cloud-download-line-duotone";
import SolarEyeScanBoldDuotone from "~icons/solar/eye-scan-bold-duotone";
import { imageUrlToBase64 } from "@/services/application";

type CandidateFile = {
  filePath?: string | null;
  fileType?: string | null;
};

const props = defineProps<{
  type: "CV" | "CL";
  title: string;
}>();

const emits = defineEmits<{
  openDocumentViewer: [file: { filePath: string; fileType: string }, fileName: string];
}>();

const { t } = useI18n();
const application = defineModel<any>({ required: true });

const file = computed<CandidateFile>(() => {
  return props.type === "CV"
    ? application.value?.resumePath || {}
    : application.value?.coverLetterPath || {};
});

const hasFile = computed(() => Boolean(file.value?.filePath));

const isPdf = computed(() => {
  if (file.value.fileType?.includes("pdf")) return true;
  return Boolean(file.value.filePath?.split("?")[0]?.toLowerCase().endsWith(".pdf"));
});

const isImage = computed(() => Boolean(file.value.fileType?.includes("image")));

const fileName = computed(() => {
  const candidate = application.value?.candidate;
  return `${props.title}-${candidate?.firstname || ""}-${candidate?.lastname || ""}`;
});

const downloadFile = async () => {
  if (!file.value.filePath) return;

  const base64 = await imageUrlToBase64(file.value.filePath);
  const extension = file.value.filePath.split(".").pop()?.split("?")[0] || "pdf";
  const downloadLink = document.createElement("a");
  downloadLink.href = base64 as string;
  downloadLink.download = `${fileName.value}.${extension}`;
  downloadLink.click();
  downloadLink.remove();
};
</script>

<template>
  <section
    class="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-base bg-fade"
  >
    <div
      class="flex shrink-0 items-center justify-between gap-2 border-b border-base bg-base px-2 py-1"
    >
      <p class="text-xs font-medium">
        {{ title }}
      </p>
      <!-- Images only: the PDF viewer and the no-preview message have their own download button -->
      <BButton
        v-if="hasFile && isImage && !isPdf"
        size="dense"
        bg="base"
        icon
        :title="t('modules.recruitment.screening.candidateDocuments.download')"
        data-cy="screening-review-document-download"
        @click="downloadFile"
      >
        <SolarCloudDownloadLineDuotone />
      </BButton>
    </div>
    <div class="relative min-h-0 flex-1">
      <div
        v-if="hasFile && isPdf && file.filePath"
        class="absolute inset-0"
        data-cy="screening-review-document-viewer"
      >
        <CandidateDocumentContainer
          variant="inline"
          :file-path="file.filePath"
          :file-name="fileName"
          scale="page-width"
        />
      </div>
      <img
        v-else-if="hasFile && isImage && file.filePath"
        :src="file.filePath"
        :alt="fileName"
        class="absolute inset-0 h-full w-full object-contain"
        data-cy="screening-review-document-viewer"
      />
      <div
        v-else-if="hasFile"
        class="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4"
        data-cy="screening-review-document-no-preview"
      >
        <SolarEyeScanBoldDuotone class="text-3xl" />
        <p class="subtitle text-center text-sm">
          {{ t("modules.recruitment.screening.candidateDocuments.noPreviewAvailable") }}
        </p>
        <BButton size="dense" bg="primary" @click="downloadFile">
          {{ t("modules.recruitment.screening.candidateDocuments.download") }}
        </BButton>
      </div>
      <div
        v-else
        class="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4"
        data-cy="screening-review-document-empty"
      >
        <p class="subtitle text-center text-sm">
          {{ t("modules.recruitment.screening.review.emptyDocument") }}
        </p>
        <CandidateDocumentButton
          v-model="application"
          :type="type"
          size="dense"
          :display="type"
        />
      </div>
    </div>
  </section>
</template>
